import React, { useState, useEffect, useRef } from "react";
import {
  CreditCard,
  DollarSign,
  Smartphone,
  Receipt,
  CheckCircle,
  XCircle,
  Banknote,
  ArrowLeftRight,
  Trash2,
  Plus,
  Calculator,
  TrendingUp,
  X,
  Users,
  UserPlus,
  Info,
} from "lucide-react";
import CustomerCaisseModal from "./CustomerCaisseModal";
import ReserveCheckoutPanel, { reserveLineKey } from "./ReserveCheckoutPanel";
import { apiFetch } from "../utils/apiClient";
import {
  clearPaymentDraftRequest,
  getOrCreatePaymentDraftRequest,
} from "../utils/paymentDraftRequest";
import { normalizeNetworkRequestError } from "../utils/networkRequestError";

// Mapping des icônes disponibles pour les moyens de paiement
const PAYMENT_ICONS = {
  banknote: Banknote,
  "dollar-sign": DollarSign,
  "credit-card": CreditCard,
  smartphone: Smartphone,
  "arrow-left-right": ArrowLeftRight,
  receipt: Receipt,
};

const PAYMENT_SETTINGS_CACHE_KEY = "moo_payment_settings_cache";
const AGGREGATOR_PAYMENT_CODES = new Set(["yango", "glovo"]);
const FULLY_DISCOUNTED_PAYMENT_METHOD = "fully_discounted";
const FULLY_DISCOUNTED_PAYMENT_LABEL = "Remise 100 %";
const COMPLIMENTARY_PAYMENT_METHOD = "complimentary";
const COMPLIMENTARY_PAYMENT_LABEL = "Offert";
const DRAFT_ORDER_TIMEOUT_MS = 12000;
const DRAFT_ORDER_OUTCOME_WAIT_MS = 6 * 60 * 1000;

const waitForDraftOrderOutcome = async (clientRequestId) => {
  const deadline = Date.now() + DRAFT_ORDER_OUTCOME_WAIT_MS;
  let lastState = "unknown";
  while (Date.now() < deadline) {
    try {
      const response = await fetch(
        `/api/order-requests/${encodeURIComponent(
          clientRequestId,
        )}/status?operation=create-order&_=${Date.now()}`,
        { cache: "no-store", credentials: "include" },
      );
      if (response.ok) {
        const outcome = await response.json().catch(() => ({}));
        lastState = String(outcome?.state || "unknown");
        if (lastState === "completed") return outcome?.payload || null;
        if (lastState === "failed") {
          const confirmedError = new Error(
            outcome?.payload?.error || "La commande n'a pas pu être enregistrée.",
          );
          confirmedError.code = outcome?.payload?.code || "ORDER_REQUEST_FAILED";
          confirmedError.status = Number(outcome?.status) || 409;
          confirmedError.currentCycle =
            Number(outcome?.payload?.currentCycle) || null;
          confirmedError.outcomeConfirmed = true;
          throw confirmedError;
        }
      }
    } catch (error) {
      if (error?.outcomeConfirmed === true) throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 900));
  }
  const error = new Error(
    "La commande est peut-être encore en cours d’enregistrement. Elle n’a pas été renvoyée afin d’éviter un doublon. Actualisez la table avant de réessayer.",
  );
  error.code = `ORDER_REQUEST_${lastState.toUpperCase()}`;
  throw error;
};

const createDraftOrderSafely = async (payload, clientRequestId) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DRAFT_ORDER_TIMEOUT_MS);
  try {
    const response = await fetch("/api/orders", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      signal: controller.signal,
      body: JSON.stringify(payload),
    });
    if (response.status === 409) {
      const conflict = await response.clone().json().catch(() => ({}));
      if (conflict?.code === "REQUEST_PENDING") {
        return await waitForDraftOrderOutcome(clientRequestId);
      }
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.error ||
          errorData.message ||
          "Erreur lors de la création de commande",
      );
      error.status = response.status;
      error.code = errorData?.code || null;
      error.currentCycle = Number(errorData?.currentCycle) || null;
      throw error;
    }
    return await response.json();
  } catch (error) {
    const normalized = normalizeNetworkRequestError(error, controller.signal);
    if (
      !["REQUEST_TIMEOUT", "REQUEST_NETWORK_UNKNOWN"].includes(
        String(normalized?.code || ""),
      )
    ) {
      throw normalized;
    }
    return await waitForDraftOrderOutcome(clientRequestId);
  } finally {
    clearTimeout(timeoutId);
  }
};

const buildFallbackCashMethod = () => ({
  id: "cash",
  name: "Espèces",
  icon: Banknote,
  color: "#10B981",
  description: "Paiement en espèces",
  isDefault: true,
  allowsChange: true,
});

const parseBooleanFlag = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0") return false;
  }
  return fallback;
};

const readPaymentSettingsCache = () => {
  try {
    const raw = localStorage.getItem(PAYMENT_SETTINGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      multiPaymentEnabled: parseBooleanFlag(parsed?.multiPaymentEnabled, false),
      creditSaleEnabled: parseBooleanFlag(parsed?.creditSaleEnabled, false),
    };
  } catch (_error) {
    return null;
  }
};

const writePaymentSettingsCache = (settings = {}) => {
  const payload = {
    multiPaymentEnabled: parseBooleanFlag(settings?.multiPaymentEnabled, false),
    creditSaleEnabled: parseBooleanFlag(settings?.creditSaleEnabled, false),
    updatedAt: Date.now(),
  };
  try {
    localStorage.setItem(PAYMENT_SETTINGS_CACHE_KEY, JSON.stringify(payload));
  } catch (_error) {
    // Ignorer les erreurs de quota/localStorage bloqué
  }
};

const DELIVERY_METHOD_CODES = {
  Direct: "",
  Yango: "YG",
  Glovo: "GV",
  "Uber Eats": "UE",
  UberEats: "UE",
  Uber: "UB",
  Bolt: "BT",
  Jumia: "JM",
};

const getAggregatorCode = (methodLabel) => {
  if (!methodLabel) return null;
  if (DELIVERY_METHOD_CODES[methodLabel]) {
    return DELIVERY_METHOD_CODES[methodLabel];
  }
  const sanitized = methodLabel.replace(/[^a-zA-Z0-9]/g, "");
  if (!sanitized) return null;
  return sanitized.slice(0, 2).toUpperCase();
};

const formatCustomerName = (customer) => {
  if (!customer) return null;
  const parts = [
    customer.civility,
    customer.first_name || customer.firstName,
    customer.last_name || customer.lastName,
    customer.name,
  ]
    .filter((part) => typeof part === "string" && part.trim())
    .map((part) => part.trim());
  if (parts.length === 0) {
    const fallbackPhone =
      customer.phone || customer.contact || customer.tel || null;
    return fallbackPhone ? fallbackPhone.toString() : null;
  }
  return parts.join(" ");
};

const getCustomerPhone = (customer) => {
  if (!customer) return null;
  return (
    customer.phone ||
    customer.contact ||
    customer.tel ||
    customer.phone_number ||
    null
  );
};

const parseVariationAttributes = (raw) => {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    } catch (_) {
      // fallback split
      const parts = raw
        .split(/[,;/]/)
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length) return parts;
    }
  } else if (Array.isArray(raw)) {
    return raw;
  } else if (typeof raw === "object") {
    return raw;
  }
  return null;
};

const isCashMethod = (method) => {
  if (!method) return false;
  const id = (method.id || "").toString().toLowerCase();
  const name = (method.name || "").toString().toLowerCase();
  return id === "cash" || name.includes("espèce");
};

const isCreditMethod = (method) => {
  const id = (method || "").toString().trim().toLowerCase();
  return id === "credit" || id === "a-credit" || id === "à-crédit";
};

const sortPaymentMethods = (methods = []) => {
  const cashMethods = methods.filter(isCashMethod);
  const creditMethods = methods.filter((m) => isCreditMethod(m?.id));
  const otherMethods = methods.filter(
    (m) => !isCashMethod(m) && !isCreditMethod(m?.id),
  );
  return [...cashMethods, ...otherMethods, ...creditMethods];
};

const getVariationDisplay = (item = {}) => {
  const baseName =
    item.name ||
    item.product_name ||
    item.productName ||
    item.title ||
    "Article";

  const variationLabelRaw =
    item.variation_label ||
    item.variationLabel ||
    item.variation_name ||
    item.variationName ||
    "";
  const variationLabel = variationLabelRaw.toString().trim();

  const attrs = parseVariationAttributes(
    item.variation_attributes || item.variationAttributes,
  );
  let attrText = "";
  if (attrs) {
    if (Array.isArray(attrs)) {
      attrText = attrs
        .map((v) => v && v.toString().trim())
        .filter(Boolean)
        .join(" / ");
    } else if (typeof attrs === "object") {
      attrText = Object.values(attrs)
        .map((v) => v && v.toString().trim())
        .filter(Boolean)
        .join(" / ");
    }
  }

  // Choisir la variation la plus pertinente
  if (
    attrText &&
    attrText !== variationLabel &&
    attrText.toLowerCase() !== "variation" &&
    attrText !== baseName
  ) {
    return attrText;
  }

  if (
    variationLabel &&
    variationLabel.toLowerCase() !== "variation" &&
    !variationLabel.toLowerCase().startsWith("variation ") &&
    variationLabel !== baseName
  ) {
    return variationLabel;
  }

  return attrText || ""; // peut être vide si rien d'utile
};

const getItemDisplayName = (item = {}) => {
  const baseName =
    item.name ||
    item.product_name ||
    item.productName ||
    item.title ||
    "Article";
  const variationText = getVariationDisplay(item);
  if (variationText && variationText !== baseName) {
    return `${baseName} · ${variationText}`;
  }
  return baseName;
};

const getExtraBadge = (type) => {
  if (type === "accompaniment") {
    return (
      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700">
        A
      </span>
    );
  }
  if (type === "supplement") {
    return (
      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">
        S
      </span>
    );
  }
  return null;
};

const PaymentInterface = ({
  order,
  onPaymentComplete,
  onClose,
  currentUser = null,
  currentSession = null,
  activeAggregator = null,
  onCounterRecoveryUpdate = null,
  onCashSessionMismatch = null,
  onTableCycleConflict = null,
}) => {
  // Ne jamais présenter un modal de paiement vide pendant que l'API charge.
  // Espèces est le moyen sûr historique et sera remplacé par la configuration
  // autoritaire dès que la réponse arrive.
  const [basePaymentMethods, setBasePaymentMethods] = useState(() => [
    buildFallbackCashMethod(),
  ]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [multiPaymentEnabled, setMultiPaymentEnabled] = useState(false);
  const [creditSaleEnabled, setCreditSaleEnabled] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [notes, setNotes] = useState("");
  const [selectedCreditor, setSelectedCreditor] = useState(null);
  const [showCreditorModal, setShowCreditorModal] = useState(false);
  const [creditorModalInitialView, setCreditorModalInitialView] = useState("list");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [animationState, setAnimationState] = useState("entering");
  const [paymentNumber, setPaymentNumber] = useState(null);
  const [authoritativeQuote, setAuthoritativeQuote] = useState(
    () => order?.authoritative_quote || order?.authoritativeQuote || null,
  );
  const [reserveRequest, setReserveRequest] = useState(null);
  const paymentInFlightRef = useRef(false);
  // Une fois le serveur revenu avec un paiement valide, le modal doit rester
  // verrouillé jusqu'à son démontage. React peut mettre un rendu à fermer le
  // modal ; sans ce verrou, un second clic relançait une cotation sur la table
  // déjà payée et affichait à tort « Aucune commande à payer ».
  const paymentSettledRef = useRef(false);
  const persistedDraftOrderRef = useRef(
    order?.counter_recovery?.created_order_id
      ? {
          orderId: order.counter_recovery.created_order_id,
          createdItems: order.counter_recovery.created_items || [],
        }
      : null,
  );
  const draftOrderRequestRef = useRef(null);
  const paymentRequestIdRef = useRef(
    order?.counter_recovery?.client_payment_request_id || null,
  );
  const recoveredCashSessionIdRef = useRef(currentSession?.id || null);
  useEffect(() => {
    if (currentSession?.id) recoveredCashSessionIdRef.current = currentSession.id;
  }, [currentSession?.id]);
  const normalizedActiveAggregator = AGGREGATOR_PAYMENT_CODES.has(
    String(activeAggregator || "").trim().toLowerCase(),
  )
    ? String(activeAggregator || "").trim().toLowerCase()
    : null;
  const isInterruptedCounterRecovery = Boolean(
    order?.fast_food_mode === true &&
      order?.counter_recovery?.stage === "payment_submitted" &&
      order?.counter_recovery?.payment_payload &&
      order?.counter_recovery?.created_order_id,
  );

  // Couleurs MOO Sync
  const colors = {
    primary: "#FFB800",
    secondary: "#FF4757",
    accent: "#FF9F43",
    success: "#2ED573",
    warning: "#FFA726",
    background: "#F5F6FA",
    cardBg: "#FFFFFF",
    dark: "#2C3E50",
    light: "#F8F9FA",
  };

  const deliveryCustomer =
    order?.delivery_customer ||
    order?.deliveryCustomer ||
    order?.customer ||
    null;

  const isDeliveryOrder = Boolean(
    order?.is_delivery ||
      order?.isDelivery ||
      order?.delivery_mode ||
      order?.deliveryMethod ||
      order?.delivery_method ||
      (typeof order?.table_id === "string" &&
        order.table_id.toString().startsWith("delivery-")) ||
      order?.table_label === "Livraison" ||
      order?.table?.isDelivery,
  );

  const headerLabel =
    order?.table_label ||
    (isDeliveryOrder
      ? "Livraison"
      : order?.table_number
        ? `Table ${order.table_number}`
        : order?.table_id
          ? `Table ${order.table_id}`
          : "Commande");

  const deliveryCustomerName = formatCustomerName(deliveryCustomer);
  const deliveryCustomerPhone =
    getCustomerPhone(deliveryCustomer) || order?.customer_phone || null;
  const deliveryCustomerAddress =
    order?.delivery_address ||
    deliveryCustomer?.address ||
    deliveryCustomer?.address_line ||
    deliveryCustomer?.location ||
    null;
  const deliveryReference =
    order?.delivery_reference ||
    order?.deliveryRef ||
    deliveryCustomer?.delivery_reference ||
    deliveryCustomer?.deliveryRef ||
    deliveryCustomer?.reference ||
    deliveryCustomer?.code ||
    null;
  const deliveryModeLabel =
    order?.delivery_mode === "takeaway" ? "À emporter" : "Livraison";
  const deliveryMethodLabelRaw =
    order?.delivery_method || order?.deliveryMethod || null;
  const deliveryMethodLabel = deliveryMethodLabelRaw
    ? deliveryMethodLabelRaw.toString()
    : null;
  const deliveryMethodHumanReadable = deliveryMethodLabel
    ? `${deliveryMethodLabel.charAt(0).toUpperCase()}${deliveryMethodLabel.slice(1)}`
    : null;
  const isAggregatorDelivery =
    isDeliveryOrder &&
    deliveryMethodLabel &&
    deliveryMethodLabel.toLowerCase() !== "direct";
  const aggregatorCode = isAggregatorDelivery
    ? getAggregatorCode(deliveryMethodLabel)
    : null;
  const aggregatorDisplayLabel =
    isAggregatorDelivery && (deliveryReference || aggregatorCode)
      ? `${aggregatorCode || ""}${deliveryReference || ""}`.trim()
      : null;
  const deliveryCardTitle = isAggregatorDelivery
    ? aggregatorDisplayLabel ||
      deliveryMethodHumanReadable ||
      "Livraison"
    : deliveryCustomerName || "Client non défini";

  const normalizedDeliveryMethodForPayload = isDeliveryOrder
    ? deliveryMethodLabel || "Direct"
    : null;
  const normalizedDeliveryModeForPayload = isDeliveryOrder
    ? order?.delivery_mode || order?.deliveryMode || "delivery"
    : null;

  const orderAdjustments = Array.isArray(order?.adjustments)
    ? order.adjustments
    : [];
  const discountAdjustments = orderAdjustments.filter(
    (adj) => adj?.type === "discount" && (Number(adj.amount) || 0) > 0,
  );
  const discountsTotal = discountAdjustments.reduce(
    (sum, adj) => sum + (Number(adj.amount) || 0),
    0,
  );
  const feeAdjustments = orderAdjustments.filter(
    (adj) => adj?.type === "fee" && (Number(adj.amount) || 0) > 0,
  );
  const feesTotal = feeAdjustments.reduce(
    (sum, adj) => sum + (Number(adj.amount) || 0),
    0,
  );

  const getExtraQuantity = (extra) =>
    Number(extra?.selectedQuantity ?? extra?.quantity ?? extra?.qty ?? 1) || 1;
  const getExtraUnitPrice = (extra) =>
    Number(extra?.price ?? extra?.unit_price ?? extra?.unitPrice ?? 0) || 0;
  const getItemUnitPrice = (item) =>
    Number(item?.price ?? item?.unit_price ?? item?.unitPrice ?? item?.product_price ?? 0) ||
    0;
  const getItemQuantity = (item) => Number(item?.quantity ?? item?.qty ?? 1) || 1;

  // Calculer le sous-total (articles + extras) puis appliquer les remises si nécessaire
  const computedSubtotal =
    typeof order?.subtotal === "number"
      ? order.subtotal
      : order?.items
        ? order.items.reduce((sum, item) => {
            const extrasPrice = Array.isArray(item.extras)
              ? item.extras.reduce(
                  (extSum, extra) =>
                    extSum + getExtraUnitPrice(extra) * getExtraQuantity(extra),
                  0,
                )
              : 0;
            const unitPrice = getItemUnitPrice(item);
            const qty = getItemQuantity(item);
            return sum + (unitPrice + extrasPrice) * qty;
          }, 0)
        : 0;

  // Montant final à payer
  const orderTotalAmount = Number(order?.total_amount);
  const hasOrderTotalAmount = Number.isFinite(orderTotalAmount);
  const computedTotalWithDiscounts = Math.max(
    0,
    computedSubtotal - discountsTotal + feesTotal,
  );
  const totalAmountRaw =
    discountsTotal > 0 || feesTotal > 0
      ? computedTotalWithDiscounts
      : hasOrderTotalAmount
        ? orderTotalAmount
        : computedSubtotal;
  const totalAmount = Number(
    authoritativeQuote?.amount_due ?? totalAmountRaw,
  ) || 0;
  const isFullyDiscounted = totalAmount <= 0.01;
  const totalBeforeDiscount = authoritativeQuote
    ? Number(authoritativeQuote.amount_due || 0) +
      Number(authoritativeQuote.discount_total || 0)
    : computedSubtotal + feesTotal;
  const isComplimentaryOrder =
    isFullyDiscounted &&
    Number.isFinite(totalBeforeDiscount) &&
    totalBeforeDiscount <= 0.01;

  const reserveUsedAmount = reserveRequest?.mode === "redeem" ? Number(reserveRequest.amount || 0) : 0;
  const externalAmountDue = Math.max(0, totalAmount - reserveUsedAmount);
  const reserveFullyCovers = reserveRequest?.mode === "redeem" && externalAmountDue === 0 && reserveUsedAmount > 0;
  const hasCreditSelected = selectedPayments.some((payment) =>
    isCreditMethod(payment?.methodId),
  );
  const hasNonCreditSelected = selectedPayments.some(
    (payment) => !isCreditMethod(payment?.methodId),
  );
  const totalPaid = selectedPayments.reduce(
    (sum, payment) =>
      isCreditMethod(payment?.methodId)
        ? sum
        : sum + (parseFloat(payment.amount) || 0),
    0,
  );
  const selectedCreditPayment = selectedPayments.find((payment) =>
    isCreditMethod(payment?.methodId),
  );
  const creditAmount = hasCreditSelected
    ? Math.max(0, parseFloat(selectedCreditPayment?.amount) || 0)
    : 0;
  const coveredAmount = totalPaid + creditAmount;
  const remainingAmount = Math.max(0, externalAmountDue - coveredAmount);
  const changeAmount = Math.max(0, coveredAmount - externalAmountDue);
  const hasCreditMethodAvailable = paymentMethods.some((method) =>
    isCreditMethod(method?.id),
  );
  const paymentMethodsGridClass = hasCreditMethodAvailable
    ? "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4"
    : "grid grid-cols-2 md:grid-cols-4 gap-4";
  const perceivedAmount = Math.min(totalPaid, externalAmountDue);
  const perceivedChange = Math.max(0, changeAmount);

  // Montants suggérés intelligents
  const suggestedAmounts = [
    Math.ceil(totalAmount / 1000) * 1000,
    Math.ceil(totalAmount / 500) * 500,
    totalAmount,
    Math.ceil(totalAmount / 100) * 100,
  ].filter(
    (amount, index, arr) =>
      arr.indexOf(amount) === index && amount >= totalAmount,
  );

  const buildCreditMethodOption = () => ({
    id: "credit",
    name: "À crédit",
    icon: Users,
    color: "#7C3AED",
    description: "Paiement différé",
    isDefault: false,
    allowsChange: false,
    isCredit: true,
  });

  useEffect(() => {
    const methods = normalizedActiveAggregator
      ? basePaymentMethods.filter(
          (method) =>
            String(method?.id || "").trim().toLowerCase() ===
            normalizedActiveAggregator,
        )
      : basePaymentMethods.filter(
          (method) =>
            !AGGREGATOR_PAYMENT_CODES.has(
              String(method?.id || "").trim().toLowerCase(),
            ),
        );
    if (!normalizedActiveAggregator && creditSaleEnabled) {
      methods.push(buildCreditMethodOption());
    }
    setPaymentMethods(sortPaymentMethods(methods));
  }, [basePaymentMethods, creditSaleEnabled, normalizedActiveAggregator]);

  useEffect(() => {
    if (!normalizedActiveAggregator) return;
    const method = paymentMethods.find(
      (entry) =>
        String(entry?.id || "").trim().toLowerCase() === normalizedActiveAggregator,
    );
    if (!method) return;
    setSelectedPayments([
      {
        id: `aggregator-${normalizedActiveAggregator}`,
        methodId: method.id,
        methodName: method.name,
        amount: totalAmount.toString(),
        icon: method.icon,
        color: method.color,
        allowsChange: false,
        isCredit: false,
      },
    ]);
    setSelectedCreditor(null);
  }, [normalizedActiveAggregator, paymentMethods, totalAmount]);

  useEffect(() => {
    if (creditSaleEnabled) return;
    if (!selectedPayments.some((payment) => isCreditMethod(payment?.methodId))) return;
    setSelectedPayments((prev) =>
      prev.filter((payment) => !isCreditMethod(payment?.methodId)),
    );
    setSelectedCreditor(null);
  }, [creditSaleEnabled, selectedPayments]);

  useEffect(() => {
    const cachedSettings = readPaymentSettingsCache();
    if (cachedSettings) {
      setMultiPaymentEnabled(cachedSettings.multiPaymentEnabled);
      setCreditSaleEnabled(cachedSettings.creditSaleEnabled);
    }

    loadPaymentMethods();
    loadPaymentSettings();
    setAnimationState("entered");
  }, []);

  useEffect(() => {
    const handleSettingsUpdate = (event) => {
      const detail = event?.detail || {};
      const hasMulti = typeof detail.multiPaymentEnabled === "boolean";
      const hasCredit = typeof detail.creditSaleEnabled === "boolean";
      if (!hasMulti && !hasCredit) return;

      const nextMulti = hasMulti ? detail.multiPaymentEnabled : multiPaymentEnabled;
      const nextCredit = hasCredit ? detail.creditSaleEnabled : creditSaleEnabled;
      setMultiPaymentEnabled(nextMulti);
      setCreditSaleEnabled(nextCredit);
      writePaymentSettingsCache({
        multiPaymentEnabled: nextMulti,
        creditSaleEnabled: nextCredit,
      });
    };

    window.addEventListener("moo-payment-settings-updated", handleSettingsUpdate);
    return () => {
      window.removeEventListener("moo-payment-settings-updated", handleSettingsUpdate);
    };
  }, [creditSaleEnabled, multiPaymentEnabled]);

  const loadPaymentMethods = async () => {
    try {
      console.log("🔄 Chargement des moyens de paiement...");
      const response = await apiFetch("/api/payment-methods", {
        includeBearer: true,
      });
      if (response.ok) {
        const data = await response.json();
        console.log("✅ Moyens de paiement reçus:", data);

        // Transformer les données pour l'interface avec les bonnes icônes
        const formattedMethods = data
          .map((method) => {
          const IconComponent = PAYMENT_ICONS[method.icon_name] || Banknote;
          return {
            id: method.code,
            name: method.name,
            icon: IconComponent,
            color: method.color,
            description: method.description,
            isDefault: method.is_default,
            allowsChange:
              method.code === "cash" ||
              method.name.toLowerCase().includes("espèce"),
          };
        });

        const sortedMethods = sortPaymentMethods(formattedMethods);
        setBasePaymentMethods(sortedMethods);
        console.log("✅ Moyens formatés:", sortedMethods);
      } else {
        console.error(
          "Erreur chargement moyens de paiement:",
          response.status,
        );
        // Fallback vers les moyens de paiement par défaut
        setBasePaymentMethods([buildFallbackCashMethod()]);
      }
    } catch (error) {
      console.error("Erreur chargement moyens de paiement:", error);
      // Fallback vers les moyens de paiement par défaut
      setBasePaymentMethods([buildFallbackCashMethod()]);
    }
  };

  const loadPaymentSettings = async () => {
    try {
      const ts = Date.now();
      const [multiResponse, creditResponse] = await Promise.all([
        apiFetch(`/api/settings/multi-payment?_=${ts}`, {
          cache: "no-store",
          includeBearer: true,
        }),
        apiFetch(`/api/settings/credit-sale?_=${ts}`, {
          cache: "no-store",
          includeBearer: true,
        }),
      ]);

      const currentCached = readPaymentSettingsCache() || {
        multiPaymentEnabled: false,
        creditSaleEnabled: false,
      };

      const nextSettings = {
        multiPaymentEnabled: currentCached.multiPaymentEnabled,
        creditSaleEnabled: currentCached.creditSaleEnabled,
      };

      if (multiResponse.ok) {
        const data = await multiResponse.json();
        nextSettings.multiPaymentEnabled = parseBooleanFlag(data?.enabled, false);
        console.log(
          "✅ Paramètres multi-paiement chargés:",
          nextSettings.multiPaymentEnabled,
        );
      } else {
        console.error("Erreur chargement paramètres multi-paiement");
      }

      if (creditResponse.ok) {
        const data = await creditResponse.json();
        nextSettings.creditSaleEnabled = parseBooleanFlag(data?.enabled, false);
        console.log(
          "✅ Paramètres vente à crédit chargés:",
          nextSettings.creditSaleEnabled,
        );
      } else {
        console.error("Erreur chargement paramètres vente à crédit");
      }

      setMultiPaymentEnabled(nextSettings.multiPaymentEnabled);
      setCreditSaleEnabled(nextSettings.creditSaleEnabled);
      writePaymentSettingsCache(nextSettings);
    } catch (error) {
      console.error("Erreur chargement paramètres de paiement:", error);
      const cachedSettings = readPaymentSettingsCache();
      if (cachedSettings) {
        setMultiPaymentEnabled(cachedSettings.multiPaymentEnabled);
        setCreditSaleEnabled(cachedSettings.creditSaleEnabled);
      } else {
        setMultiPaymentEnabled(false);
        setCreditSaleEnabled(false);
      }
    }
  };

  useEffect(() => {
    if (!hasCreditSelected || selectedCreditor) return;
    const fallbackCreditor =
      order?.customer ||
      order?.delivery_customer ||
      order?.deliveryCustomer ||
      null;
    if (!fallbackCreditor) return;
    const hasIdentity =
      fallbackCreditor?.id ||
      fallbackCreditor?.phone ||
      fallbackCreditor?.contact;
    if (hasIdentity) {
      setSelectedCreditor(fallbackCreditor);
    }
  }, [hasCreditSelected, selectedCreditor, order]);

  const addPaymentMethod = (methodId) => {
    const method = paymentMethods.find((m) => m.id === methodId);
    if (!method) return;
    const methodIsCredit = isCreditMethod(methodId);
    if (methodIsCredit && externalAmountDue - totalPaid <= 0.01) return;

    const newPayment = {
      id: Date.now(),
      methodId: methodId,
      methodName: method.name,
      amount: methodIsCredit
        ? Math.max(0, externalAmountDue - totalPaid).toString()
        : "",
      icon: method.icon,
      color: method.color,
      allowsChange: method.allowsChange,
      isCredit: methodIsCredit,
    };

    setSelectedPayments([...selectedPayments, newPayment]);
  };

  const removePaymentMethod = (paymentId) => {
    setSelectedPayments(selectedPayments.filter((p) => p.id !== paymentId));
  };

  const updatePaymentAmount = (paymentId, amount) => {
    setSelectedPayments(
      selectedPayments.map((payment) =>
        payment.id === paymentId ? { ...payment, amount: amount } : payment,
      ),
    );
  };

  const quickSetAmount = (paymentId, amount) => {
    updatePaymentAmount(paymentId, amount.toString());
  };

  const validatePayment = () => {
    if (reserveRequest) {
      if (!["issue", "redeem"].includes(reserveRequest.mode)) return { valid: false, message: "Choisissez le type de consignation" };
      if (reserveRequest.mode === "issue" && !reserveRequest.selections?.length) return { valid: false, message: "Sélectionnez les articles à mettre en réserve" };
      if (reserveRequest.mode === "redeem" && (!reserveRequest.reserveId || reserveUsedAmount <= 0 || reserveUsedAmount > totalAmount)) return { valid: false, message: "Vérifiez la réserve et son montant" };
      if (reserveFullyCovers && selectedPayments.length === 0) return { valid: true };
    }
    if (isFullyDiscounted) return { valid: true };

    if (selectedPayments.length === 0) {
      return {
        valid: false,
        message: "Veuillez sélectionner au moins un moyen de paiement",
      };
    }

    if (hasCreditSelected && !selectedCreditor) {
      return {
        valid: false,
        message: "Veuillez sélectionner un créancier pour la vente à crédit",
      };
    }

    // Vérifier que tous les montants sont saisis
    const invalidPayments = selectedPayments.filter(
      (p) => !p.amount || parseFloat(p.amount) <= 0,
    );
    if (invalidPayments.length > 0) {
      return {
        valid: false,
        message:
          "Veuillez saisir un montant valide pour tous les moyens de paiement",
      };
    }

    // Séparer les paiements espèces et non-espèces
    const cashPayments = selectedPayments.filter((p) => p.allowsChange);
    const nonCashPayments = selectedPayments.filter(
      (p) => !p.allowsChange && !isCreditMethod(p.methodId),
    );

    // Calculer les totaux
    const totalNonCash = nonCashPayments.reduce(
      (sum, p) => sum + parseFloat(p.amount),
      0,
    );

    // Règle 1: Le total des moyens non-espèces ne peut pas dépasser le montant total de la commande
    const amountToCollect = Math.max(0, externalAmountDue - creditAmount);
    if (totalNonCash > amountToCollect + 0.01) {
      return {
        valid: false,
        message:
          "Le total des paiements non-espèces dépasse la part à encaisser",
      };
    }

    // Règle 2: Si le total payé dépasse le montant de la commande, il doit y avoir de l'espèce pour la monnaie
    if (changeAmount > 0.01 && cashPayments.length === 0) {
      return {
        valid: false,
        message:
          "Seul le paiement en espèces peut générer de la monnaie à rendre",
      };
    }

    // Règle 3: Le montant total payé doit couvrir au minimum le montant de la commande
    if (remainingAmount > 0.01) {
      return {
        valid: false,
        message: `Montant insuffisant. Il manque ${remainingAmount.toLocaleString()} F`,
      };
    }

    return { valid: true };
  };

  const handlePayment = async () => {
    if (
      loading ||
      paymentInFlightRef.current ||
      paymentSettledRef.current
    ) {
      return;
    }

    const interruptedCounterPayment =
      order?.fast_food_mode === true &&
      order?.counter_recovery?.stage === "payment_submitted" &&
      order?.counter_recovery?.payment_payload &&
      order?.counter_recovery?.created_order_id;
    if (interruptedCounterPayment) {
      paymentInFlightRef.current = true;
      setLoading(true);
      try {
        let savedPayload = {
          ...order.counter_recovery.payment_payload,
          sessionId:
            recoveredCashSessionIdRef.current ||
            order.counter_recovery.payment_payload.sessionId,
        };
        let response = await fetch("/api/payments", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(savedPayload),
        });
        let result = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (
            response.status === 409 &&
            result?.code === "CASH_SESSION_MISMATCH"
          ) {
            let recoveredSession = null;
            if (typeof onCashSessionMismatch === "function") {
              recoveredSession = await onCashSessionMismatch();
            }
            if (
              recoveredSession?.id &&
              typeof onCounterRecoveryUpdate === "function"
            ) {
              recoveredCashSessionIdRef.current = recoveredSession.id;
              savedPayload = {
                ...savedPayload,
                sessionId: recoveredSession.id,
              };
              onCounterRecoveryUpdate({
                payment_payload: savedPayload,
              });
              response = await fetch("/api/payments", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(savedPayload),
              });
              result = await response.json().catch(() => ({}));
            }
          }
        }
        if (!response.ok) {
          throw new Error(
            result.error ||
              result.message ||
              "Le paiement interrompu n’a pas pu être confirmé.",
          );
        }
        paymentSettledRef.current = true;
        const orderId = order.counter_recovery.created_order_id;
        let productionDispatch = null;
        try {
          const dispatchResponse = await fetch(
            `/api/orders/${orderId}/dispatch-production`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({}),
            },
          );
          const dispatchPayload = await dispatchResponse.json().catch(() => ({}));
          productionDispatch = dispatchResponse.ok
            ? { success: dispatchPayload?.success !== false, results: [dispatchPayload] }
            : { success: false, results: [dispatchPayload] };
        } catch (dispatchError) {
          productionDispatch = {
            success: false,
            results: [{ orderId, error: dispatchError?.message }],
          };
        }
        onPaymentComplete({
          reserve: result?.reserve || null,
          amount: Number(result?.amount_due ?? result?.amount ?? savedPayload.amount_due),
          totalPaid: Number(result?.amount_due ?? result?.amount ?? savedPayload.amount_due),
          amountReceived: Number(
            result?.amount_received ?? savedPayload.amount_received ?? savedPayload.amount_due,
          ),
          changeGiven: Number(result?.change_given ?? savedPayload.change_given ?? 0),
          paymentNumber: result?.paymentNumber || result?.payment_number || null,
          anchorOrderId: result?.anchorOrderId || orderId,
          orderIds: savedPayload.orderIds || [orderId],
          tableId: null,
          tableNumber: "Comptoir",
          fastFoodMode: true,
          productionDispatch,
          paymentMethods: savedPayload.payment_breakdown || [],
        });
      } catch (error) {
        showNotification(error.message || "Reprise du paiement impossible", "error");
      } finally {
        if (!paymentSettledRef.current) {
          paymentInFlightRef.current = false;
          setLoading(false);
        }
      }
      return;
    }

    const validation = validatePayment();
    if (!validation.valid) {
      showNotification(validation.message, "error");
      return;
    }

    paymentInFlightRef.current = true;
    setLoading(true);
    try {
      const tableId = order?.table_id || order?.tableId;
      const isFastFoodOrder =
        order?.fast_food_mode === true ||
        order?.fastFoodMode === true;
      const orderItems = Array.isArray(order?.items) ? order.items : [];
      const draftItems = orderItems.filter(
        (item) =>
          item &&
          !item.order_item_id &&
          !item.orderItemId &&
          !item.order_itemId,
      );

      const toNumber = (value, fallback = 0) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : fallback;
      };

      const buildOrderItemPayload = (item) => {
        const productId =
          item.product_id ?? item.productId ?? item.id ?? item.product?.id ?? null;
        return {
          productId,
          clientLineId:
            item.client_line_id ??
            item.clientLineId ??
            null,
          quantity: toNumber(item.quantity ?? item.qty ?? 1, 1),
          unitPrice: toNumber(
            item.price ?? item.unitPrice ?? item.unit_price ?? item.product_price ?? 0,
            0,
          ),
          destination: item.destination || item.department || null,
          extras: Array.isArray(item.extras) ? item.extras : [],
          variationId:
            item.variation_id ??
            item.variationId ??
            item.default_variation_id ??
            item.selectedVariationId ??
            null,
          variationName:
            item.variation_name ??
            item.variationName ??
            item.variation_label ??
            item.variationLabel ??
            null,
          variationAttributes:
            item.variation_attributes ?? item.variationAttributes ?? null,
          variationLabel: item.variation_label ?? item.variationLabel ?? null,
        };
      };

      // ✅ IMPORTANT POS: si des items sont encore en “draft?, ne pas les persister à l’ouverture.
      // On crée la commande seulement quand le caissier confirme le paiement.
      let createdOrderId =
        persistedDraftOrderRef.current?.orderId || null;
      let createdItems =
        persistedDraftOrderRef.current?.createdItems || [];
      const creditCustomerName = selectedCreditor
        ? formatCustomerName(selectedCreditor)
        : null;
      const creditCustomerPhone = selectedCreditor
        ? getCustomerPhone(selectedCreditor)
        : null;
      const creditCustomerId =
        selectedCreditor && Number.isFinite(Number(selectedCreditor.id))
          ? Number(selectedCreditor.id)
          : null;
      if (draftItems.length > 0 && !createdOrderId) {
        const canCreateWithoutTable = isFastFoodOrder || isDeliveryOrder;
        if (!tableId && !canCreateWithoutTable) {
          throw new Error("Impossible d'identifier la table pour créer la commande.");
        }

        const transformedDraftItems = draftItems.map(buildOrderItemPayload);
        const invalidDraftItems = transformedDraftItems.filter(
          (item) =>
            !Number.isFinite(Number(item.productId)) ||
            Number(item.productId) <= 0,
        );
        if (invalidDraftItems.length > 0) {
          console.error("Articles draft invalides avant paiement:", {
            invalidDraftItems,
            draftItems,
          });
          throw new Error(
            "Certains articles du panier ne peuvent pas être sauvegardés. Le paiement est interrompu pour préserver la commande.",
          );
        }
        const transformedItems = transformedDraftItems;

        if (transformedItems.length === 0) {
          throw new Error("Aucun article draft valide à sauvegarder.");
        }

        const draftScopeKey = isFastFoodOrder
          ? `counter:${order?.counter_transaction_id || "pending"}`
          : isDeliveryOrder
            ? `delivery:${order?.delivery_reference || order?.table_id || "pending"}`
            : `table:${tableId}`;
        const draftRequest = isFastFoodOrder
          ? {
              requestId: order?.counter_transaction_id || null,
              storageKey: null,
            }
          : getOrCreatePaymentDraftRequest({
              storage:
                typeof window !== "undefined" ? window.localStorage : null,
              scopeKey: draftScopeKey,
              items: transformedItems,
            });
        draftOrderRequestRef.current = draftRequest;

        const createOrderPayload = {
          tableId: isFastFoodOrder ? null : tableId || null,
          items: transformedItems,
          sessionId: currentSession?.id || null,
          visitorCount: isFastFoodOrder
            ? null
            : order?.visitor_count || order?.visitorCount || null,
          customerId: order?.customer_id || order?.customerId || null,
          customerPhone: order?.customer_phone || order?.customerPhone || null,
          isDelivery: isDeliveryOrder,
          deliveryMode: normalizedDeliveryModeForPayload,
          deliveryMethod: normalizedDeliveryMethodForPayload,
          deliveryCustomer: deliveryCustomer || null,
          fastFoodMode: isFastFoodOrder,
          orderType: isFastFoodOrder
            ? "takeaway"
            : isDeliveryOrder
              ? "delivery"
              : order?.order_type || null,
          suppressDispatch: isFastFoodOrder,
          counterTransactionId: isFastFoodOrder
            ? order?.counter_transaction_id || null
            : null,
          originZoneId: isFastFoodOrder
            ? order?.origin_zone_id || null
            : null,
          client_request_id: draftRequest.requestId,
          occupancy_cycle:
            order?.occupancy_cycle ?? order?.occupancyCycle ?? null,
        };

        if (hasCreditSelected) {
          createOrderPayload.customerId = creditCustomerId || createOrderPayload.customerId;
          createOrderPayload.customerPhone =
            creditCustomerPhone || createOrderPayload.customerPhone;
        }

        console.log("🧾 Création commande (draft→DB) avant paiement:", {
          tableId: isFastFoodOrder ? "Comptoir" : tableId,
          draftCount: draftItems.length,
        });

        let createResult;
        try {
          createResult = await createDraftOrderSafely(
            createOrderPayload,
            draftRequest.requestId,
          );
        } catch (createError) {
          if (
            createError?.status === 409 &&
            createError?.code === "CASH_SESSION_MISMATCH"
          ) {
            if (typeof onCashSessionMismatch === "function") {
              await onCashSessionMismatch();
            }
            throw new Error(
              createError.message ||
                "La session de caisse a changé. Vérifiez-la puis réessayez.",
            );
          }
          throw createError;
        }
        createdOrderId = createResult?.orderId || null;
        createdItems = Array.isArray(createResult?.createdItems)
          ? createResult.createdItems
          : [];
        if (!createdOrderId) {
          throw new Error(
            "La sauvegarde des nouveaux articles n'a pas été confirmée. Le paiement est interrompu pour préserver le panier.",
          );
        }
        persistedDraftOrderRef.current = {
          orderId: createdOrderId,
          createdItems,
        };
        if (isFastFoodOrder && typeof onCounterRecoveryUpdate === "function") {
          onCounterRecoveryUpdate({
            stage: "order_created",
            created_order_id: createdOrderId,
            created_items: createdItems,
          });
        }
      }

      // ✅ Paiement unique côté serveur, même en multi-moyens
      const baseOrderIds = Array.isArray(order?.orderIds) ? order.orderIds : [];
      const orderIds = createdOrderId
        ? Array.from(new Set([...baseOrderIds, createdOrderId].filter(Boolean)))
        : baseOrderIds;
      const createdItemIdByClientLine = new Map(
        (createdItems || []).map((entry) => [
          String(entry?.client_line_id || ""),
          Number(entry?.order_item_id) || null,
        ]),
      );
      const resolvedAdjustments = orderAdjustments.map((adjustment) => {
        const directOrderItemId = Number(
          adjustment?.order_item_id ?? adjustment?.orderItemId,
        );
        const mappedOrderItemId = createdItemIdByClientLine.get(
          String(adjustment?.client_line_id || ""),
        );
        return {
          ...adjustment,
          order_item_id:
            (Number.isInteger(directOrderItemId) && directOrderItemId > 0
              ? directOrderItemId
              : mappedOrderItemId) || null,
        };
      });
      const quoteResponse = await fetch("/api/payments/quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: order?.id || createdOrderId || null,
          orderIds,
          tableId: isFastFoodOrder ? null : tableId || null,
          adjustments: resolvedAdjustments,
          splitSelection: order?.splitSelection || order?.split_selection || null,
        }),
      });
      const quotePayload = await quoteResponse.json().catch(() => ({}));
      if (!quoteResponse.ok) {
        throw new Error(
          quotePayload.error ||
            quotePayload.message ||
            "Impossible de vérifier le total de la commande.",
        );
      }
      if (
        Math.abs(Number(totalAmount) - Number(quotePayload.amount_due)) > 0.01
      ) {
        setAuthoritativeQuote(quotePayload);
        setSelectedPayments([]);
        paymentRequestIdRef.current = null;
        showNotification(
          `Montant actualisé : ${Number(
            quotePayload.amount_due || 0,
          ).toLocaleString()} F. Choisissez le moyen de paiement.`,
          "info",
        );
        return;
      }
      setAuthoritativeQuote(quotePayload);
      const isComplimentarySettlement =
        Number(quotePayload.amount_due) <= 0.01 &&
        Number(quotePayload.amount_due || 0) +
          Number(quotePayload.discount_total || 0) <=
          0.01;
      const zeroSettlementLabel = isComplimentarySettlement
        ? COMPLIMENTARY_PAYMENT_LABEL
        : FULLY_DISCOUNTED_PAYMENT_LABEL;
      const zeroSettlementMethod = isComplimentarySettlement
        ? COMPLIMENTARY_PAYMENT_METHOD
        : FULLY_DISCOUNTED_PAYMENT_METHOD;
      const combinedLabel = reserveFullyCovers ? "Réserve"
        : isFullyDiscounted
        ? zeroSettlementLabel
        : selectedPayments.length === 1
          ? selectedPayments[0].methodName
          : selectedPayments.map((p) => p.methodName).join(" + ");
      const primaryMethodId = reserveFullyCovers ? "reserve"
        : isFullyDiscounted
        ? zeroSettlementMethod
        : selectedPayments.length === 1
          ? selectedPayments[0].methodId
          : "multi";

      const breakdownTag =
        selectedPayments.length > 1
          ? ` | MOO_BREAKDOWN=${selectedPayments
              .map((p) => {
                const amt = isCreditMethod(p.methodId)
                  ? creditAmount
                  : parseFloat(p.amount);
                const safeAmount = Number.isFinite(amt) ? amt : 0;
                return `${p.methodId}:${safeAmount}`;
              })
              .join(",")}`
          : "";

      let paymentData = {
        ...(reserveRequest ? { reserve: reserveRequest.mode === "issue" ? {
          mode: "issue", customerId: reserveRequest.customerId || null,
          selections: reserveRequest.selections.map((selection) => {
            const item = (order?.items || []).find((entry) => reserveLineKey(entry) === selection.lineKey);
            const mapped = createdItemIdByClientLine.get(String(item?.client_line_id || selection.lineKey));
            return { orderItemId: mapped || Number(item?.order_item_id || item?.orderItemId || item?.id), quantity: selection.quantity };
          }),
        } : reserveRequest } : {}),
        orderId: order?.id || createdOrderId || null,
        orderIds,
        tableId: isFastFoodOrder ? null : tableId || null,
        amount: quotePayload.amount_due,
        amount_due: quotePayload.amount_due,
        quote_version: quotePayload.quote_version,
        adjustments: resolvedAdjustments,
        splitSelection: order?.splitSelection || order?.split_selection || null,
        method: primaryMethodId,
        userId: currentUser?.id || null,
        notes:
          notes ||
          `Paiement ${combinedLabel} - Table ${
            order?.table_id || order?.tableId || "N/A"
          }${breakdownTag}`,
        amountReceived: totalPaid,
        amount_received: totalPaid,
        changeGiven: changeAmount > 0 ? changeAmount : 0,
        change_given: changeAmount > 0 ? changeAmount : 0,
        discountAmount: discountsTotal > 0 ? discountsTotal : null,
        payment_breakdown: isFullyDiscounted
          ? []
          : selectedPayments.map((payment) => ({
              method: payment.methodId,
              amount: isCreditMethod(payment.methodId)
                ? creditAmount
                : Number(payment.amount) || 0,
            })),
        client_payment_request_id:
          paymentRequestIdRef.current ||
          (() => {
            const value =
              typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : `pay-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            paymentRequestIdRef.current = value;
            return value;
          })(),
        sessionId: recoveredCashSessionIdRef.current || currentSession?.id || null,
        isDelivery: isDeliveryOrder,
        deliveryMethod: normalizedDeliveryMethodForPayload,
        deliveryMode: normalizedDeliveryModeForPayload,
        creditCustomerId: hasCreditSelected ? creditCustomerId : null,
        creditCustomerName: hasCreditSelected ? creditCustomerName : null,
        creditCustomerPhone: hasCreditSelected ? creditCustomerPhone : null,
      };

      const recoveryPaymentPayload = order?.counter_recovery?.payment_payload;
      if (
        isFastFoodOrder &&
        order?.counter_recovery?.stage === "payment_submitted" &&
        recoveryPaymentPayload &&
        typeof recoveryPaymentPayload === "object"
      ) {
        paymentData = {
          ...recoveryPaymentPayload,
          client_payment_request_id:
            order.counter_recovery.client_payment_request_id ||
            recoveryPaymentPayload.client_payment_request_id,
        };
        paymentRequestIdRef.current = paymentData.client_payment_request_id;
      }

      if (isFastFoodOrder && typeof onCounterRecoveryUpdate === "function") {
        onCounterRecoveryUpdate({
          stage: "payment_submitted",
          created_order_id: createdOrderId,
          created_items: createdItems,
          client_payment_request_id: paymentData.client_payment_request_id,
          payment_payload: paymentData,
        });
      }

      console.log("💳 Envoi paiement unique au serveur:", {
        paymentData,
        orderIdsLength: orderIds.length,
        hasTableId: !!paymentData.tableId,
      });

      const response = await fetch("/api/payments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(paymentData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (
          response.status === 409 &&
          errorData?.code === "PAYMENT_TOTAL_CHANGED" &&
          errorData?.quote
        ) {
          setAuthoritativeQuote(errorData.quote);
          setSelectedPayments([]);
          paymentRequestIdRef.current = null;
          showNotification(
            `La commande vient d’être actualisée. Montant à payer : ${Number(
              errorData.quote.amount_due || 0,
            ).toLocaleString()} F.`,
            "info",
          );
          return;
        }
        if (
          response.status === 409 &&
          errorData?.code === "CASH_SESSION_MISMATCH"
        ) {
          if (typeof onCashSessionMismatch === "function") {
            const recoveredSession = await onCashSessionMismatch();
            if (recoveredSession?.id) {
              recoveredCashSessionIdRef.current = recoveredSession.id;
              if (isFastFoodOrder && typeof onCounterRecoveryUpdate === "function") {
                onCounterRecoveryUpdate({
                  payment_payload: {
                    ...paymentData,
                    sessionId: recoveredSession.id,
                  },
                });
              }
            }
          }
          showNotification(
            errorData.error ||
              "La session de caisse a changé. Vérifiez-la puis confirmez à nouveau.",
            "error",
          );
          return;
        }
        const errorMessage =
          errorData.error || errorData.message || "Erreur lors du paiement";
        console.error("Erreur serveur paiement:", {
          status: response.status,
          statusText: response.statusText,
          errorData,
        });
        throw new Error(errorMessage);
      }

      const result = await response.json();
      paymentSettledRef.current = true;
      clearPaymentDraftRequest({
        storage: typeof window !== "undefined" ? window.localStorage : null,
        storageKey: draftOrderRequestRef.current?.storageKey,
      });

      let productionDispatchResult = null;
      if (isFastFoodOrder) {
        const dispatchOrderIds = Array.from(
          new Set((orderIds || []).filter(Boolean)),
        );
        const dispatchResults = [];
        for (const dispatchOrderId of dispatchOrderIds) {
          try {
            const dispatchRes = await fetch(
              `/api/orders/${dispatchOrderId}/dispatch-production`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
              },
            );
            const dispatchPayload = await dispatchRes.json().catch(() => ({}));
            if (!dispatchRes.ok) {
              throw new Error(
                dispatchPayload.error ||
                  dispatchPayload.message ||
                  "Envoi production non confirmé",
              );
            }
            dispatchResults.push(dispatchPayload);
          } catch (dispatchError) {
            console.error("Erreur dispatch production fast-food:", dispatchError);
            dispatchResults.push({
              success: false,
              orderId: dispatchOrderId,
              error: dispatchError?.message || "Envoi production non confirmé",
            });
          }
        }
        productionDispatchResult = {
          success: dispatchResults.every((entry) => entry?.success !== false),
          results: dispatchResults,
        };
        if (!productionDispatchResult.success) {
          showNotification(
            `${
              isFullyDiscounted ? "Commande finalisée" : "Paiement validé"
            }, mais l'envoi cuisine/bar doit être vérifié.`,
            "error",
          );
        }
      }

      if (
        (!productionDispatchResult || productionDispatchResult.success) &&
        result &&
        result.paymentNumber
      ) {
        setPaymentNumber(result.paymentNumber);
        showNotification(
          `${
            isFullyDiscounted
              ? isComplimentarySettlement
                ? "Commande offerte finalisée"
                : "Commande finalisée avec une remise de 100 %"
              : "Paiement effectué avec succès"
          } ! Numéro : ${result.paymentNumber}`,
          "success",
        );
      } else if (!productionDispatchResult || productionDispatchResult.success) {
        showNotification(
          isFullyDiscounted
            ? isComplimentarySettlement
              ? "Commande offerte finalisée."
              : "Commande finalisée avec une remise de 100 %."
            : "Paiement effectué avec succès !",
          "success",
        );
      }

      const confirmedAmountDue = Number(
        result?.amount_due ?? result?.amount ?? totalAmount,
      );
      const confirmedAmountReceived = Number(
        result?.amount_received ?? result?.amountReceived ?? perceivedAmount,
      );
      const confirmedChange = Number(
        result?.change_given ?? result?.changeGiven ?? perceivedChange,
      );
      let changeToRemove = Math.max(0, confirmedChange);
      const settledPaymentMethods = isFullyDiscounted
        ? [{ method: zeroSettlementLabel, amount: 0 }]
        : selectedPayments.map((payment) => {
            const rawAmount = isCreditMethod(payment.methodId)
              ? Number(result?.credit_amount ?? result?.creditAmount ?? creditAmount)
              : Number(payment.amount) || 0;
            const changePart = payment.allowsChange
              ? Math.min(rawAmount, changeToRemove)
              : 0;
            changeToRemove -= changePart;
            return {
              method: payment.methodName,
              amount: Math.max(0, rawAmount - changePart),
            };
          });
      onPaymentComplete({
        reserve: result?.reserve || null,
        amount: confirmedAmountDue,
        totalPaid: confirmedAmountDue,
        amountReceived: Number(
          confirmedAmountReceived,
        ),
        changeGiven: confirmedChange,
        paymentNumber: result?.paymentNumber || null,
        anchorOrderId: result?.anchorOrderId || createdOrderId || null,
        paymentMethods: settledPaymentMethods,
        creditAmount: Number(result?.credit_amount ?? result?.creditAmount ?? creditAmount),
        creditCustomerName: hasCreditSelected ? creditCustomerName : null,
        creditCustomerPhone: hasCreditSelected ? creditCustomerPhone : null,
        sessionId: result?.sessionId || currentSession?.id || null,
        orderIds: orderIds || [],
        tableId: isFastFoodOrder ? null : order?.table_id || order?.tableId || null,
        tableNumber: isFastFoodOrder
          ? "Comptoir"
          : isDeliveryOrder
            ? order?.table_label || order?.table_number || order?.tableNumber || null
            : order?.table_number || order?.tableNumber || null,
        isDelivery: order?.is_delivery || order?.isDelivery || isDeliveryOrder || false,
        fastFoodMode: isFastFoodOrder,
        productionDispatch: productionDispatchResult,
        splitSettlement: result?.splitSettlement || null,
      });
    } catch (error) {
      console.error("Erreur paiement:", error);
      if (
        ["TABLE_CYCLE_REQUIRED", "TABLE_CYCLE_CHANGED"].includes(
          String(error?.code || ""),
        ) &&
        typeof onTableCycleConflict === "function"
      ) {
        onTableCycleConflict({
          currentCycle: Number(error?.currentCycle) || null,
          error,
        });
        return;
      }
      showNotification(error.message || "Erreur lors du paiement", "error");
    } finally {
      if (!paymentSettledRef.current) {
        paymentInFlightRef.current = false;
        setLoading(false);
      }
    }
  };

  const formatCurrency = (amount) => {
    return (
      new Intl.NumberFormat("fr-FR", {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount) + " F"
    );
  };

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleClose = () => {
    setAnimationState("exiting");
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // Rendu du modal
  return (
    <div
      className={`fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4
      ${animationState === "entering" ? "animate-fadeIn" : ""}
      ${animationState === "exiting" ? "animate-fadeOut" : ""}`}
    >
      <div
        className={`relative flex w-full max-w-4xl max-h-[95vh] flex-col overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#020617] via-[#020b1f] to-[#020617] text-white
        ${animationState === "entering" ? "animate-slideInFromBottom" : ""}
        ${animationState === "exiting" ? "animate-slideOutToBottom" : ""}`}
      >
        <div className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-amber-500/10 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
        </div>

        {/* Header Premium avec gradient MOO */}
        <div className="relative shrink-0 overflow-hidden border-b border-white/10 bg-white/5 backdrop-blur-xl">
          <div
            className="relative z-10 p-6 sm:p-8 text-white bg-gradient-to-r from-[#0b1120] via-[#020617] to-[#020617]"
          >
            {/* Éléments décoratifs */}
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-20 translate-x-16 -translate-y-20 bg-amber-400 blur-2xl" />
            <div className="absolute bottom-0 left-0 w-40 h-40 rounded-full opacity-10 -translate-x-10 translate-y-10 bg-cyan-400 blur-2xl" />

            <div className="relative z-20 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-lg bg-black/20 backdrop-blur-md border border-white/10">
                  💳
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-amber-300">
                    Paiement caisse
                  </p>
                  <h2 className="mt-1 text-2xl sm:text-3xl font-bold">
                    Paiement MOO Sync
                  </h2>
                  <p className="mt-1 text-sm sm:text-base text-white/80">
                    {headerLabel} • {formatCurrency(totalAmount)}
                  </p>
                  {multiPaymentEnabled && (
                    <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-100 text-xs font-medium">
                      Multi-paiement activé
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={handleClose}
                aria-label="Fermer le paiement"
                className="mt-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 border border-white/15 backdrop-blur-md hover:bg-white/20 transition-all duration-200 group"
              >
                <X className="w-6 h-6 group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="relative z-10 min-h-0 flex-1 overflow-y-auto p-6 sm:p-8">
          {/* Résumé de la commande */}
          <div className="mb-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white flex items-center">
                <Receipt className="w-5 h-5 mr-2" />
                Résumé de la commande
              </h3>
              <div
                className="text-2xl font-bold"
                style={{ color: colors.primary }}
              >
                {formatCurrency(totalAmount)}
              </div>
            </div>

            {!isAggregatorDelivery && <ReserveCheckoutPanel items={order?.items || []} value={reserveRequest} invoiceAmount={totalAmount} disabled={loading} onChange={(value) => {
              setReserveRequest(value); setSelectedPayments([]); paymentRequestIdRef.current = null;
            }} />}
            {reserveRequest?.mode === "redeem" && <p className="mb-4 text-amber-200">Réserve utilisée : {formatCurrency(reserveUsedAmount)} · Complément à encaisser : {formatCurrency(externalAmountDue)}</p>}
            {discountsTotal > 0 && (
              <div className="mb-5 rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="flex justify-between text-sm text-white/70">
                  <span>Sous-total</span>
                  <span className="font-semibold text-white">
                    {formatCurrency(computedSubtotal)}
                  </span>
                </div>
                <div className="mt-2 flex justify-between text-sm text-emerald-200">
                  <span>Réductions</span>
                  <span className="font-semibold">
                    -{formatCurrency(discountsTotal)}
                  </span>
                </div>
                {discountAdjustments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {discountAdjustments.map((adj, index) => (
                      <div
                        key={`${adj.id || "disc"}-${index}`}
                        className="flex justify-between text-xs text-white/60"
                      >
                        <span>
                          {(adj.scope === "item" ? "Article" : "Panier")}
                          {adj.targetLabel ? ` · ${adj.targetLabel}` : ""}
                        </span>
                        <span className="font-semibold text-emerald-200">
                          -{formatCurrency(Number(adj.amount) || 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {isDeliveryOrder && (
              <div className="mb-6 rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-white/60">
                  Client à livrer
                </div>
                <div className="mt-2 text-sm font-semibold text-white">
                  {deliveryCardTitle}
                </div>
                {!isAggregatorDelivery && (
                  <div className="text-sm text-white/70">
                    {deliveryCustomerPhone || "Contact non renseigné"}
                  </div>
                )}
                {deliveryCustomerAddress && (
                  <div className="mt-1 text-sm text-white/60">
                    {deliveryCustomerAddress}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-white/60">
                  <span>Mode : {deliveryModeLabel}</span>
                  {deliveryMethodHumanReadable && (
                    <span>
                      Méthode : {deliveryMethodHumanReadable}
                    </span>
                  )}
                  {deliveryReference && (
                    <span>Réf. client : {deliveryReference}</span>
                  )}
                </div>
              </div>
            )}

            {order?.items && order.items.length > 0 && (
              <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                {order.items.map((item, index) => {
                  // Calculer le prix avec extras
                  const extrasPrice = item.extras
                    ? item.extras.reduce(
                        (sum, extra) =>
                          sum + (extra.price || 0) * (extra.quantity || 1),
                        0,
                      )
                    : 0;
                  const itemTotalPrice =
                    (item.price + extrasPrice) * item.quantity;

                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex justify-between text-sm text-gray-300">
                        <span>
                          {item.quantity}x {getItemDisplayName(item)}
                        </span>
                        <span>{formatCurrency(itemTotalPrice)}</span>
                      </div>
                      {item.extras && item.extras.length > 0 && (
                        <div className="ml-4 space-y-1">
                          {item.extras.map((extra, extraIndex) => (
                            <div
                              key={extraIndex}
                              className="flex justify-between text-xs text-blue-300"
                            >
                              <span className="flex items-center">
                                + {extra.quantity || 1}x {extra.name}
                                {getExtraBadge(extra.type)}
                              </span>
                              <span>
                                {formatCurrency(
                                  (extra.price || 0) * (extra.quantity || 1),
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sélection des moyens de paiement */}
          {isFullyDiscounted ? (
            <div className="rounded-2xl border border-emerald-300/40 bg-emerald-500/10 p-6">
              <h3 className="text-lg font-semibold text-emerald-200">
                {isComplimentaryOrder
                  ? "Commande offerte"
                  : "Commande entièrement remise"}
              </h3>
              <p className="mt-2 text-sm text-white/70">
                Aucun moyen de paiement n’est nécessaire. La vente sera
                enregistrée à 0 F avec la mention «{" "}
                {isComplimentaryOrder ? COMPLIMENTARY_PAYMENT_LABEL : FULLY_DISCOUNTED_PAYMENT_LABEL} ».
              </p>
            </div>
          ) : multiPaymentEnabled ? (
            <div className="space-y-6">
              {/* Moyens de paiement disponibles */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center text-white">
                  <Plus
                    className="w-5 h-5 mr-2"
                    style={{ color: colors.primary }}
                  />
                  Ajouter un moyen de paiement
                </h3>
                <div className={paymentMethodsGridClass}>
                  {paymentMethods.map((method) => {
                    const IconComponent = method.icon;
                    const isSelected = selectedPayments.some(
                      (p) => p.methodId === method.id,
                    );
                    const methodIsCredit = isCreditMethod(method.id);
                    const isDisabledByCreditRule =
                      methodIsCredit && externalAmountDue - totalPaid <= 0.01;
                    const isDisabled = isSelected || isDisabledByCreditRule;

                    return (
                      <button
                        key={method.id}
                        onClick={() => !isDisabled && addPaymentMethod(method.id)}
                        disabled={isDisabled}
                        className={`p-4 rounded-2xl border transition-all duration-300 hover:-translate-y-0.5 ${
                          isDisabled
                            ? "border-emerald-300/60 bg-emerald-500/10 opacity-70 cursor-not-allowed"
                            : "border-white/10 bg-white/5 hover:border-amber-300/60 hover:bg-amber-500/10 hover:shadow-lg"
                        }`}
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center text-white"
                            style={{ backgroundColor: method.color }}
                          >
                            <IconComponent className="w-6 h-6" />
                          </div>
                          <span className="text-sm font-medium text-white">
                            {method.name}
                          </span>
                          {isSelected && (
                            <span className="text-xs text-white/70">
                              Déjà ajouté
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Moyens de paiement sélectionnés */}
              {selectedPayments.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center text-white">
                    <Calculator
                      className="w-5 h-5 mr-2"
                      style={{ color: colors.primary }}
                    />
                    Moyens de paiement sélectionnés
                  </h3>
                  <div className="space-y-4">
                    {selectedPayments.map((payment) => {
                      const IconComponent = payment.icon;
                      const maxAmount = payment.isCredit
                        ? externalAmountDue
                        : payment.allowsChange
                        ? Math.max(totalAmount, 10000)
                        : externalAmountDue;

                      return (
                        <div
                          key={payment.id}
                          className="p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-sm"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div
                                className="w-12 h-12 rounded-full flex items-center justify-center text-white"
                                style={{ backgroundColor: payment.color }}
                              >
                                <IconComponent className="w-6 h-6" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-white">
                                  {payment.methodName}
                                </h4>
                                <p className="text-sm text-white/60">
                                  {payment.isCredit
                                    ? "Reste à payer"
                                    : payment.allowsChange
                                      ? "Accepte la monnaie"
                                      : "Montant exact"}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => removePaymentMethod(payment.id)}
                              className="w-8 h-8 rounded-full bg-red-900/30 text-red-400 hover:bg-red-900/50 transition-colors flex items-center justify-center"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="space-y-3">
                            <div className="relative">
                              <input
                                type="number"
                                value={payment.isCredit ? creditAmount : payment.amount}
                                onChange={(e) =>
                                  updatePaymentAmount(
                                    payment.id,
                                    e.target.value,
                                  )
                                }
                                placeholder="Montant"
                                className="w-full px-4 py-3 text-xl font-semibold border-2 border-white/15 rounded-xl focus:border-amber-300 focus:outline-none transition-colors bg-white/5 text-white"
                                min="0"
                                max={maxAmount}
                                readOnly={payment.isCredit && !hasNonCreditSelected}
                              />
                              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">
                                F
                              </div>
                            </div>

                            {/* Montants suggérés */}
                            {payment.allowsChange && !payment.isCredit && (
                              <div className="flex flex-wrap gap-2">
                                {suggestedAmounts.map((amount) => (
                                  <button
                                    key={amount}
                                    onClick={() =>
                                      quickSetAmount(payment.id, amount)
                                    }
                                    className="px-3 py-1 bg-blue-900/30 text-blue-300 rounded-lg text-sm font-medium hover:bg-blue-900/50 transition-colors"
                                  >
                                    {formatCurrency(amount)}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Mode paiement simple
            <div className="space-y-6">
              {selectedPayments.length === 0 ? (
                <>
                  <h3 className="text-lg font-semibold mb-4 text-white">
                    Sélectionnez un moyen de paiement
                  </h3>
                  <div className={paymentMethodsGridClass}>
                    {paymentMethods.map((method) => {
                      const IconComponent = method.icon;
                      const methodIsCredit = isCreditMethod(method.id);
                      const isDisabledByCreditRule =
                        methodIsCredit && externalAmountDue - totalPaid <= 0.01;

                      return (
                        <button
                          key={method.id}
                          onClick={() => {
                            if (isDisabledByCreditRule) {
                              return;
                            }
                            setSelectedPayments([
                              {
                                id: Date.now(),
                                methodId: method.id,
                                methodName: method.name,
                                amount: methodIsCredit
                                  ? remainingAmount.toString()
                                  : externalAmountDue.toString(),
                                icon: method.icon,
                                color: method.color,
                                allowsChange: method.allowsChange,
                                isCredit: methodIsCredit,
                              },
                            ]);
                          }}
                          className={`p-6 rounded-2xl border transition-all duration-300 hover:-translate-y-0.5 ${
                            isDisabledByCreditRule
                              ? "border-emerald-300/60 bg-emerald-500/10 opacity-70 cursor-not-allowed"
                              : "border-white/10 bg-white/5 hover:border-amber-300/60 hover:bg-amber-500/10 hover:shadow-lg"
                          }`}
                          disabled={isDisabledByCreditRule}
                        >
                          <div className="flex flex-col items-center space-y-3">
                            <div
                              className="w-16 h-16 rounded-full flex items-center justify-center text-white"
                              style={{ backgroundColor: method.color }}
                            >
                              <IconComponent className="w-8 h-8" />
                            </div>
                            <span className="font-medium text-gray-200">
                              {method.name}
                            </span>
                            <span className="text-sm text-gray-400">
                              {method.description}
                            </span>
                            {isDisabledByCreditRule && (
                              <span className="text-xs text-white/70">
                                Incompatible avec le cr?dit
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                // Affichage du moyen sélectionné en mode simple
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center text-white">
                    <Calculator
                      className="w-5 h-5 mr-2"
                      style={{ color: colors.primary }}
                    />
                    Paiement sélectionné
                  </h3>
                  {selectedPayments.map((payment) => {
                    const IconComponent = payment.icon;

                      return (
                        <div
                          key={payment.id}
                          className="p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-sm"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center text-white"
                              style={{ backgroundColor: payment.color }}
                            >
                              <IconComponent className="w-6 h-6" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-white">
                                {payment.methodName}
                              </h4>
                              <p className="text-sm text-gray-400">
                                {payment.allowsChange
                                  ? "Accepte la monnaie"
                                  : "Montant exact"}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedPayments([])}
                            className="w-8 h-8 rounded-full bg-red-900/30 text-red-400 hover:bg-red-900/50 transition-colors flex items-center justify-center"
                            title="Changer de moyen de paiement"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                          <div className="space-y-3">
                            <div className="relative">
                              <input
                              type="number"
                              value={payment.amount}
                              onChange={(e) =>
                                updatePaymentAmount(payment.id, e.target.value)
                              }
                                placeholder="Montant"
                                className="w-full px-4 py-3 text-xl font-semibold border-2 border-white/15 rounded-xl focus:border-amber-300 focus:outline-none transition-colors bg-white/5 text-white"
                              min="0"
                              readOnly={payment.isCredit}
                            />
                            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">
                              F
                            </div>
                          </div>

                          {/* Montants suggérés */}
                          {payment.allowsChange && !payment.isCredit && (
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() =>
                                  quickSetAmount(payment.id, totalAmount)
                                }
                                className="px-3 py-1 bg-blue-900/30 text-blue-300 rounded-lg text-sm font-medium hover:bg-blue-900/50 transition-colors"
                              >
                                Montant exact ({formatCurrency(totalAmount)})
                              </button>
                              {suggestedAmounts.map((amount) => (
                                <button
                                  key={amount}
                                  onClick={() =>
                                    quickSetAmount(payment.id, amount)
                                  }
                                  className="px-3 py-1 bg-blue-900/30 text-blue-300 rounded-lg text-sm font-medium hover:bg-blue-900/50 transition-colors"
                                >
                                  {formatCurrency(amount)}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Récapitulatif des montants */}
          {selectedPayments.length > 0 && (
            <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center text-white">
                <TrendingUp className="w-5 h-5 mr-2 text-blue-400" />
                Récapitulatif
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Montant total commande</span>
                  <span className="font-semibold text-white">
                    {formatCurrency(totalAmount)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Montant perçu</span>
                  <span className="font-semibold text-blue-400">
                    {formatCurrency(perceivedAmount)}
                  </span>
                </div>

                {remainingAmount > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-orange-400">
                      {hasCreditSelected ? "À crédit" : "Montant restant"}
                    </span>
                    <span className="font-semibold text-orange-400">
                      {formatCurrency(remainingAmount)}
                    </span>
                  </div>
                )}

                {perceivedChange > 0 && (
                  <div className="flex justify-between items-center p-3 bg-green-900/30 rounded-lg border border-green-700/50">
                    <span className="text-green-400 font-medium">
                      Monnaie à rendre
                    </span>
                    <span className="font-bold text-green-400 text-lg">
                      {formatCurrency(perceivedChange)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {hasCreditSelected && (
            <div className="mt-6 rounded-2xl border border-purple-400/30 bg-purple-500/10 p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-200" />
                  <div>
                    <p className="text-sm font-semibold text-purple-100">
                      Créancier (obligatoire)
                    </p>
                    <p className="text-xs text-purple-200/80">
                      Sélectionnez un contact du répertoire pour enregistrer la vente à crédit.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setCreditorModalInitialView("list");
                      setShowCreditorModal(true);
                    }}
                    className="px-3 py-2 rounded-xl border border-purple-300/40 text-purple-100 hover:bg-purple-500/20 transition"
                  >
                    Répertoire
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCreditorModalInitialView("add");
                      setShowCreditorModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-purple-500/30 border border-purple-300/50 text-purple-50 hover:bg-purple-500/40 transition"
                  >
                    <UserPlus className="w-4 h-4" />
                    Nouveau
                  </button>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4">
                {selectedCreditor ? (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-white">
                      {formatCustomerName(selectedCreditor) || "Créancier sélectionné"}
                    </p>
                    <p className="text-xs text-white/70">
                      {getCustomerPhone(selectedCreditor) || "Téléphone non renseigné"}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-red-200">
                    Aucun créancier sélectionné.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-white/80 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ajouter une note pour ce paiement..."
              className="w-full px-4 py-3 border border-white/15 rounded-xl focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none bg-white/5 text-white placeholder-white/40"
              rows="3"
            />
          </div>
        </div>

        {/* Footer avec boutons d'action */}
        <div className="relative z-10 shrink-0 p-6 sm:p-8 bg-white/5 border-t border-white/10 backdrop-blur-xl">
          <div className="flex space-x-4">
            <button
              onClick={handleClose}
              className="flex-1 py-4 px-6 rounded-2xl font-semibold border border-white/10 bg-white/5 text-white hover:bg-white/10 transition-all duration-300 hover:-translate-y-0.5"
            >
              Annuler
            </button>

            <button
              onClick={handlePayment}
              disabled={
                loading ||
                (!isInterruptedCounterRecovery &&
                  ((!isFullyDiscounted && !reserveFullyCovers && selectedPayments.length === 0) ||
                    remainingAmount > 0.01 ||
                    (hasCreditSelected && !selectedCreditor)))
              }
              className={`flex-1 py-4 px-6 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center ${
                loading ||
                (!isInterruptedCounterRecovery &&
                  ((!isFullyDiscounted && !reserveFullyCovers && selectedPayments.length === 0) ||
                    remainingAmount > 0.01 ||
                    (hasCreditSelected && !selectedCreditor)))
                  ? "bg-white/5 text-white/40 cursor-not-allowed border border-white/10"
                  : "bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 hover:shadow-lg hover:shadow-amber-500/30 border border-amber-300/60"
              }`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Traitement...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {isInterruptedCounterRecovery
                    ? "Reprendre le paiement"
                    : isFullyDiscounted
                      ? "Finaliser la commande"
                      : "Confirmer le paiement"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Affichage du numéro de paiement */}
      {paymentNumber && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <Receipt className="text-white" size={20} />
            </div>
            <div>
              <h4 className="font-bold text-green-800">Numéro de facture</h4>
              <p className="text-2xl font-bold text-green-600">{paymentNumber}</p>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <div
          className={`fixed left-1/2 top-4 z-[70] w-[min(92vw,42rem)] -translate-x-1/2 p-4 rounded-2xl shadow-lg transition-all duration-300 ${
            notification.type === "success"
              ? "bg-green-600 text-white border border-green-500"
              : notification.type === "info"
                ? "bg-blue-600 text-white border border-blue-500"
                : "bg-red-600 text-white border border-red-500"
          }`}
        >
          <div className="flex items-center space-x-3">
            {notification.type === "success" ? (
              <CheckCircle className="w-5 h-5" />
            ) : notification.type === "info" ? (
              <Info className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      <CustomerCaisseModal
        isOpen={showCreditorModal}
        onClose={() => setShowCreditorModal(false)}
        onSelectCustomer={(customer) => {
          setSelectedCreditor(customer);
          setShowCreditorModal(false);
        }}
        initialView={creditorModalInitialView}
      />

      {/* Styles personnalisés */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        @keyframes slideInFromBottom {
          from {
            opacity: 0;
            transform: translateY(100px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes slideOutToBottom {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(100px) scale(0.9);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-fadeOut {
          animation: fadeOut 0.3s ease-out;
        }

        .animate-slideInFromBottom {
          animation: slideInFromBottom 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .animate-slideOutToBottom {
          animation: slideOutToBottom 0.3s ease-in;
        }
      `}</style>
    </div>
  );
};

export default PaymentInterface;
